<template>
  <Modal
    class="scoped"
    full-height
    :modal-style="{ maxWidth: '900px' }"
    title="Bulk Apply Notifications"
  >
    <div class="card q-px-lg q-py-sm col media-types-cell">
      <div>
        <QToggle
          v-model="mobile"
          class="row"
          label="Mobile"
        />
      </div>

      <QToggle
        v-model="email"
        class="col"
        label="Email"
      />

      <div class="row q-mt-md">
        <label
          v-for="mediaType in media"
          :key="mediaType"
        >
          <input
            v-model="mediaTypes[`${mediaType}_content`]"
            hidden
            :name="mediaType"
            type="checkbox"
          />
          <span
            class="chip"
            :class="classFor(mediaType)"
          >
            {{ capitalize(mediaType) }}
          </span>
        </label>
      </div>
      <span class="q-pt-md block">
        * This will override any existing notifications for the selected users
      </span>
    </div>
    <QTable
      :columns="columns"
      :loading="loading"
      row-key="name"
      :rows="filteredStreamNotifications"
      :rows-per-page-options="rowPerPage"
      table-style="table-layout: fixed"
      title="Stream Notifications"
    >
      <template #top-row>
        <QTr class="filter-row">
          <QTd />
          <QTd>
            <QInput
              v-model="filters['id']"
              borderless
              clearable
              dense
              placeholder="Search"
              square
            />
          </QTd>
          <QTd>
            <QInput
              v-model="filters['stream.id']"
              borderless
              clearable
              dense
              placeholder="Search"
              square
            />
          </QTd>
          <QTd>
            <QInput
              v-model="filters['stream.label']"
              borderless
              clearable
              dense
              placeholder="Search"
              square
            />
          </QTd>
          <QTd>
            <QInput
              v-model="filters['user.name']"
              borderless
              clearable
              dense
              placeholder="Search"
              square
            />
          </QTd>
        </QTr>
      </template>

      <template #body="props">
        <QTr :props="props">
          <QTd
            key="selected"
            class="wrap-table-content"
            :props="props"
          >
            <QCheckbox v-model="props.row.selected" />
          </QTd>
          <QTd
            key="id"
            class="wrap-table-content"
            :props="props"
          >
            {{ props.row.id }}
          </QTd>
          <QTd
            key="stream_id"
            class="wrap-table-content"
            :props="props"
          >
            {{ props.row.stream.id }}
          </QTd>
          <QTd
            key="stream_label"
            class="wrap-table-content"
            :props="props"
          >
            {{ props.row.stream.label }}
          </QTd>
          <QTd
            key="user_name"
            class="wrap-table-content"
            :props="props"
          >
            {{ props.row.user.name }}
          </QTd>
        </QTr>
      </template>
    </QTable>

    <template #buttons>
      <BaseButton
        color="primary"
        label="Save"
        new-design
        @click="save"
      />
    </template>
  </Modal>
</template>

<script>
import BaseButton from "shared/components/base/BaseButton.vue";
import Modal from "shared/components/core/modals/Modal.vue";
import { tableFilter } from "shared/helpers/object";
import { capitalize } from "shared/helpers/string";
import StreamNotification from "shared/resources/StreamNotification";

export default {
  name: "BulkApplyNotificationsModal",
  components: {
    Modal,
    BaseButton,
  },
  props: {
    organisationId: {
      type: [Number, String],
      required: true,
    },
  },
  emits: ["confirm"],
  data() {
    return {
      loading: false,
      mobile: false,
      email: false,
      streamNotifications: [],
      media: [
        "print",
        "magazine",
        "online",
        "tv",
        "radio",
        "social",
        "podcast",
      ],
      mediaTypes: {},
      rowPerPage: [25, 50, 100],
      filters: {
        id: "",
        stream_id: "",
        stream_label: "",
        user_name: "",
      },
      columns: [
        {
          name: "selected",
          required: true,
          label: "",
          align: "left",
          field: "selected",
          style: "width: 5%",
          classes: "wrap-table-content",
        },
        {
          name: "id",
          required: true,
          label: "ID",
          align: "left",
          field: "stream.id",
          sortable: true,
          style: "width: 5%",
          classes: "wrap-table-content",
        },
        {
          name: "stream_id",
          required: true,
          label: "Stream ID",
          align: "left",
          field: "stream.id",
          sortable: true,
          style: "width: 5%",
          classes: "wrap-table-content",
        },
        {
          name: "stream_label",
          required: true,
          label: "Stream Label",
          align: "left",
          field: "stream.label",
          sortable: true,
          style: "width: 5%",
          classes: "wrap-table-content",
        },
        {
          name: "user_name",
          required: true,
          label: "User Name",
          align: "left",
          field: "user.name",
          sortable: true,
          style: "width: 10%",
          classes: "wrap-table-content",
        },
      ],
    };
  },
  computed: {
    filteredStreamNotifications() {
      return this.streamNotifications.filter((streamNotification) =>
        tableFilter(streamNotification, this.filters)
      );
    },
    selectedStreamNotifications() {
      return this.streamNotifications.filter(
        (streamNotification) => streamNotification.selected
      );
    },
  },
  mounted() {
    this.mediaTypes = this.media.reduce(
      (obj, type) => ({
        ...obj,
        [`${type}_content`]: type !== "social",
      }),
      {}
    );

    this.loadStreamNotifications();
  },
  methods: {
    capitalize,
    async loadStreamNotifications() {
      try {
        this.loading = true;

        const filters = {
          organisationId: { match: this.organisationId },
        };

        const streamNotifications = (
          await StreamNotification.includes(["stream", "user"])
            .where(filters)
            .per(999)
            .stats({ total: "count" })
            .all()
        ).data;

        this.streamNotifications = streamNotifications.map(
          (streamNotification) => ({
            ...streamNotification,
            selected: false,
          })
        );
      } catch (error) {
        this.$q.notify({
          message: `Error: ${error.message}`,
          icon: "error",
          color: "red",
        });
      } finally {
        this.loading = false;
      }
    },
    async save(cancel) {
      try {
        this.loading = true;

        const params = {
          ids: this.selectedStreamNotifications.map(
            (streamNotification) => streamNotification.id
          ),
          mobile: this.mobile,
          email: this.email,
          media_types: this.mediaTypes,
        };

        await this.$streemApiAdmin.post("stream_notifications/bulk_update", {
          params,
        });

        this.$emit("confirm");
      } catch (error) {
        this.$q.notify({
          message: `Error: ${error.message}`,
          icon: "error",
          color: "red",
        });

        cancel();
      } finally {
        this.loading = false;
      }
    },
    classFor(mediaType) {
      const disabled = !this.mobile && !this.email;
      const field = `${mediaType}_content`;

      return [
        {
          [`disabled-${mediaType}`]: !this.mediaTypes[field],
          "notifications-disabled": disabled || !this.mediaTypes[field],
        },
        `checkbox-${mediaType}`,
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
.media-types-cell {
  :deep(label) {
    padding: 2px;
  }
}

.notifications-disabled {
  opacity: 0.2;
}
</style>
